import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Grid, Chip,
  FormControl, InputLabel, Select, MenuItem, CircularProgress
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const CreateProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { setSelectedProjectId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    manager_id: '',
    validator_id: '',
    contributors: []
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/users/list');
        setUsers(res.data);
      } catch (err) {
        console.error('Failed to load users:', err);
        toast.error('Failed to load team members');
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (isEdit) {
      api.get(`/projects/${id}`).then(res => {
        const p = res.data;
        setFormData({
          name: p.name || '',
          description: p.description || '',
          manager_id: p.manager_id || '',
          validator_id: p.validator_id || '',
          contributors: p.contributors || []
        });
      }).catch(() => toast.error('Failed to load project')).finally(() => setFetching(false));
    }
  }, [id, isEdit]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        manager_id: formData.manager_id || null,
        validator_id: formData.validator_id || null,
        contributors: formData.contributors
      };
      if (isEdit) {
        await api.patch(`/projects/${id}`, payload);
        toast.success('Project updated');
      } else {
        const res = await api.post('/projects', payload);
        toast.success('Project created');
        setSelectedProjectId(res.data.id);
      }
      navigate('/projects');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleContributorsChange = (e) => {
    const value = e.target.value;
    if (value.includes('__all__')) {
      setFormData(prev => ({ ...prev, contributors: users.map(u => u.id) }));
    } else {
      setFormData(prev => ({ ...prev, contributors: value }));
    }
  };

  if (fetching) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button onClick={() => navigate('/projects')} startIcon={<ArrowBack />} variant="outlined" size="small">
          Back
        </Button>
        <Typography variant="h5" fontWeight={600} color="#2c3e50">
          {isEdit ? '✏️ Edit Project' : '🆕 Create New Project'}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3, borderRadius: '10px', border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#1abc9c' }}>
          📋 Project Information
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Project Name *" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="e.g., P&C Pricing Model 2026" size="small" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Description" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Describe the project purpose, scope, and objectives..." multiline rows={3} size="small" />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, borderRadius: '10px', border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#1abc9c' }}>
          👥 Team Members
        </Typography>
        
        {loadingUsers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Manager</InputLabel>
                <Select value={formData.manager_id} onChange={(e) => handleChange('manager_id', e.target.value)} label="Manager">
                  <MenuItem value=""><em>None</em></MenuItem>
                  {users.map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.email} {u.job_title ? `(${u.job_title})` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Validator</InputLabel>
                <Select value={formData.validator_id} onChange={(e) => handleChange('validator_id', e.target.value)} label="Validator">
                  <MenuItem value=""><em>None</em></MenuItem>
                  {users.filter(u => u.is_validator || u.role === 'enterprise_admin').map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.email} {u.job_title ? `(${u.job_title})` : ''} {u.is_validator ? '✅' : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Contributors</InputLabel>
                <Select multiple value={formData.contributors} onChange={handleContributorsChange} label="Contributors"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((uid) => {
                        const user = users.find(u => u.id === uid);
                        return user ? <Chip key={uid} label={user.email} size="small" onDelete={() => {
                          setFormData(prev => ({ ...prev, contributors: prev.contributors.filter(c => c !== uid) }));
                        }} /> : null;
                      })}
                    </Box>
                  )}
                >
                  <MenuItem value="__all__"><em>Select All</em></MenuItem>
                  {users.map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.email} {u.job_title ? `(${u.job_title})` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={() => navigate('/projects')} variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading || !formData.name.trim()} variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <Save />}
          sx={{ bgcolor: '#1abc9c', '&:hover': { bgcolor: '#16a085' } }}>
          {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Project'}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateProject;
